import { AutoImportServiceKey, ProjectsServiceKey } from "@app/injectionKeys";
import { injectStrict } from "@app/utils/injectStrict";
import { AutoImportService } from "@core/application/autoImportService";
import { ProjectsService } from "@core/application/projects.service";
import { ReviewPreset } from "@core/domain/types/review-preset.type";
import { Timeframe } from "@core/domain/types/timeframe.type";
import { computed, InjectionKey, ref, watch } from "vue";

export const CreateReviewKey: InjectionKey<ReturnType<typeof useCreateReview>> =
  Symbol("CreateReview");

export enum CreateReviewStep {
  PresetSelection,
  PmsDeviceLevelMinimal,
  MedicalBackgroundIndication,
  NotifiedBody,
  Summary,
  AssistantsSettings,
}

type CreatePresetModel = {
  preset: ReviewPreset;
  deviceName: string;
  manufacturerName: string;
  purpose: string;
  isAutoScreeningPaused: boolean;
  isAutoExtractionPaused: boolean;
  isAutoSynthesisPaused: boolean;
  conditionName: string;
  alternativeConditionNames: string[];
  targetLoe: string;
  timeRange: Timeframe | undefined;
  timeLimitFromDate?: string;
  timeLimitToDate?: string;
  alternativeName: string;
  alternativeDeviceNames: string[];
  alternativeManufacturerNames: string[];
  safetyInformationSources: string[];
  deviceGroup: string;
  alternativeDeviceGroupNames: string[];
};

export enum HasAlternativeNames {
  Na,
  Yes,
  No,
}

const isReviewAutomated = ref<boolean>(true);

export default function useCreateReview(projectId: number) {
  const projectsService: ProjectsService = injectStrict(ProjectsServiceKey);
  const autoImportService: AutoImportService = injectStrict(AutoImportServiceKey);

  const reviewPresets: {
    id: ReviewPreset;
    title: string;
    subtitle: string;
    description: string;
    isAutomated?: boolean;
  }[] = [
    {
      id: ReviewPreset.DEVICE_SPECIFIC_SEARCH,
      title: "Post-market",
      subtitle: "Device under evaluation safety and performance",
      description: "This literature search identifies post-market data on the device.",
      isAutomated: true,
    },
    {
      id: ReviewPreset.DEVICE_SPECIFIC_SEARCH,
      title: "Post-market",
      subtitle: "Similar device safety and performance",
      description: "This literature search identifies post-market data on the device.",
      isAutomated: true,
    },
    {
      id: ReviewPreset.DEVICE_SPECIFIC_SEARCH,
      title: "Post-market",
      subtitle: "Equivalent device safety and performance",
      description: "This literature search identifies post-market data on the device.",
      isAutomated: true,
    },

    {
      id: ReviewPreset.MEDICAL_BACKGROUND,
      title: "State of the art",
      subtitle: "Medical background of a condition",
      description:
        "This review provides a comprehensive summary of the medical context, including prevalence and relevant patient populations for a particular indication or condition, outlining the role of relevant medical interventions (treatment, diagnosis, etc.).",
      isAutomated: true,
    },

    {
      id: ReviewPreset.NOTIFIED_BODY,
      title: "State of the art",
      subtitle: "Notified body",
      description: "",
      isAutomated: true,
    },
  ];

  const automatedStepFlow: CreateReviewStep[] = [
    CreateReviewStep.PresetSelection,
    CreateReviewStep.PmsDeviceLevelMinimal,
    CreateReviewStep.MedicalBackgroundIndication,
    CreateReviewStep.NotifiedBody,
    CreateReviewStep.AssistantsSettings,
    CreateReviewStep.Summary,
  ];

  const manualStepFlow: CreateReviewStep[] = [CreateReviewStep.Summary];

  const stepFlow = computed(() => (isReviewAutomated.value ? automatedStepFlow : manualStepFlow));

  const currentStepIndex = ref(0);

  const currentStep = computed<CreateReviewStep>({
    get: () => stepFlow.value[currentStepIndex.value],
    set: (step: CreateReviewStep) => {
      const index = stepFlow.value.indexOf(step);
      if (index !== -1) currentStepIndex.value = index;
    },
  });

  const reviewName = ref<string>("");
  const levelOfEvidence = ref("");
  const timeFrame = ref(0);
  const preset = ref<CreatePresetModel>({
    preset: ReviewPreset.DEFAULT,
    deviceName: "",
    manufacturerName: "",
    alternativeDeviceNames: [],
    alternativeManufacturerNames: [],
    alternativeName: "",
    conditionName: "",
    alternativeConditionNames: [],
    targetLoe: "",
    timeRange: undefined,
    timeLimitFromDate: "",
    timeLimitToDate: "",
    purpose: "",
    safetyInformationSources: [],
    isAutoExtractionPaused: true,
    isAutoScreeningPaused: true,
    isAutoSynthesisPaused: true,
    deviceGroup: "",
    alternativeDeviceGroupNames: [],
  });

  const medicalBackgroundConditionNames = ref<string[]>([]);

  function deleteMedicalBackgroundConditionName(name: string) {
    const index = medicalBackgroundConditionNames.value.indexOf(name);
    if (index !== -1) {
      medicalBackgroundConditionNames.value.splice(index, 1);
    }
  }
  const isGatheringBackgroundInformation = ref(false);

  const deviceHasAlternativeNames = ref<HasAlternativeNames>(HasAlternativeNames.Na);
  const manufacturerHasAlternativeNames = ref<HasAlternativeNames>(HasAlternativeNames.Na);

  watch(
    () => preset.value.preset,
    (v) => {
      reset(v);
    },
  );

  function reset(presetId: ReviewPreset = ReviewPreset.DEFAULT) {
    currentStepIndex.value = 0;
    preset.value = {
      preset: presetId,
      deviceName: "",
      manufacturerName: "",
      alternativeDeviceNames: [],
      alternativeManufacturerNames: [],
      alternativeName: "",
      conditionName: "",
      alternativeConditionNames: [],
      targetLoe: "",
      timeRange: undefined,
      timeLimitFromDate: "",
      timeLimitToDate: "",
      purpose: "",
      safetyInformationSources: [],
      isAutoExtractionPaused: true,
      isAutoScreeningPaused: true,
      isAutoSynthesisPaused: true,
      deviceGroup: "",
      alternativeDeviceGroupNames: [],
    };
    deviceHasAlternativeNames.value = HasAlternativeNames.Na;
    manufacturerHasAlternativeNames.value = HasAlternativeNames.Na;
  }

  async function createReview(afterReviewSeriesId?: string) {
    const reviewId = await projectsService.createReview(
      projectId,
      reviewName.value,
      preset.value,
      afterReviewSeriesId,
    );

    return reviewId;
  }

  async function* autoImportPmsItems({
    reviewId,
    safetyInformationSourceIds,
  }: {
    reviewId: number;
    safetyInformationSourceIds: string[];
  }) {
    for await (const message of autoImportService.handlePmsWithProgress({
      reviewId,
      safetyInformationSourceIds,
    })) {
      yield message;
    }
  }

  async function* autoImportMedicalBackgroundItems(reviewId: number) {
    for await (const message of autoImportService.handleMedicalBackgroundWithProgress(reviewId)) {
      yield message;
    }
  }

  async function* autoImportNotifiedBodyItems(reviewId: number) {
    for await (const message of autoImportService.handleNotifiedBodyWithProgress(reviewId)) {
      yield message;
    }
  }

  function isDeviceNamesValid() {
    return (
      preset.value.deviceName !== "" &&
      (deviceHasAlternativeNames.value === HasAlternativeNames.No ||
        (deviceHasAlternativeNames.value === HasAlternativeNames.Yes &&
          preset.value.alternativeDeviceNames.length > 0))
    );
  }

  function isManufacturerNamesValid() {
    return (
      preset.value.manufacturerName !== "" &&
      (manufacturerHasAlternativeNames.value === HasAlternativeNames.No ||
        (manufacturerHasAlternativeNames.value === HasAlternativeNames.Yes &&
          preset.value.alternativeManufacturerNames.length > 0))
    );
  }

  function isPresetSelectionValid() {
    return preset.value.preset !== ReviewPreset.DEFAULT;
  }

  function isPmsDeviceAndManufacturerNamesValid() {
    return isDeviceNamesValid() && isManufacturerNamesValid();
  }

  function isMedicalBackgroundIndicationValid() {
    return (
      preset.value.conditionName !== "" &&
      !isGatheringBackgroundInformation.value &&
      preset.value.purpose !== "" &&
      preset.value.targetLoe !== "" &&
      preset.value.timeRange !== undefined
    );
  }

  function isSummaryValid() {
    return reviewName.value !== "";
  }

  function isNotifiedBodyValid() {
    return (
      preset.value.deviceGroup !== "" &&
      !isGatheringBackgroundInformation.value &&
      preset.value.targetLoe !== "" &&
      preset.value.timeRange !== undefined
    );
  }

  const isCurrentStepValid = computed<boolean>(() => {
    if (isGatheringBackgroundInformation.value) {
      return false;
    }
    switch (currentStep.value) {
      case CreateReviewStep.PresetSelection:
        return isPresetSelectionValid();
      case CreateReviewStep.PmsDeviceLevelMinimal:
        return isPmsDeviceAndManufacturerNamesValid();
      case CreateReviewStep.MedicalBackgroundIndication:
        return isMedicalBackgroundIndicationValid();
      case CreateReviewStep.NotifiedBody:
        return isNotifiedBodyValid();
      case CreateReviewStep.AssistantsSettings:
        return true;
      case CreateReviewStep.Summary:
        return isSummaryValid();
      default:
        return false;
    }
  });

  function goToNextStep() {
    if (!isCurrentStepValid.value) return;

    switch (currentStep.value) {
      case CreateReviewStep.PresetSelection:
        handlePresetSelection();
        break;
      case CreateReviewStep.Summary:
        break;
      case CreateReviewStep.MedicalBackgroundIndication:
      case CreateReviewStep.PmsDeviceLevelMinimal:
      case CreateReviewStep.NotifiedBody:
        currentStep.value = CreateReviewStep.AssistantsSettings;
        break;
      default:
        currentStep.value = CreateReviewStep.Summary;
        break;
    }
  }

  function generateReviewName() {
    if (preset.value.preset === ReviewPreset.DEVICE_SPECIFIC_SEARCH) {
      reviewName.value = `${preset.value.deviceName} - post-market search`;
    } else if (preset.value.preset === ReviewPreset.MEDICAL_BACKGROUND) {
      reviewName.value = `${preset.value.conditionName} - state of the art`;
    } else if (preset.value.preset === ReviewPreset.NOTIFIED_BODY) {
      reviewName.value = `${preset.value.deviceGroup} - state of the art`;
    }
  }

  function handlePresetSelection() {
    switch (preset.value.preset) {
      case ReviewPreset.DEVICE_SPECIFIC_SEARCH:
        currentStep.value = CreateReviewStep.PmsDeviceLevelMinimal;
        break;
      case ReviewPreset.MEDICAL_BACKGROUND:
        currentStep.value = CreateReviewStep.MedicalBackgroundIndication;
        break;
      case ReviewPreset.NOTIFIED_BODY:
        currentStep.value = CreateReviewStep.NotifiedBody;
        break;
      case ReviewPreset.DEFAULT:
      case ReviewPreset.CUSTOM:
        currentStep.value = CreateReviewStep.Summary;
        break;
    }
  }

  function goToPreviousStep() {
    switch (currentStep.value) {
      case CreateReviewStep.PmsDeviceLevelMinimal:
        currentStep.value = CreateReviewStep.PresetSelection;
        break;
      case CreateReviewStep.MedicalBackgroundIndication:
        currentStep.value = CreateReviewStep.PresetSelection;
        break;
      case CreateReviewStep.NotifiedBody:
        currentStep.value = CreateReviewStep.PresetSelection;
        break;
      case CreateReviewStep.AssistantsSettings:
        if (preset.value.preset === ReviewPreset.MEDICAL_BACKGROUND) {
          currentStep.value = CreateReviewStep.MedicalBackgroundIndication;
        }
        if (preset.value.preset === ReviewPreset.DEVICE_SPECIFIC_SEARCH) {
          currentStep.value = CreateReviewStep.PmsDeviceLevelMinimal;
        }
        if (preset.value.preset === ReviewPreset.NOTIFIED_BODY) {
          currentStep.value = CreateReviewStep.NotifiedBody;
        }
        break;
      case CreateReviewStep.Summary:
        handleSummaryPreviousStep();
        break;
      case CreateReviewStep.PresetSelection:
        break;
    }
  }

  function handleSummaryPreviousStep() {
    switch (preset.value.preset) {
      case ReviewPreset.DEVICE_SPECIFIC_SEARCH:
        currentStep.value = CreateReviewStep.AssistantsSettings;
        break;
      case ReviewPreset.MEDICAL_BACKGROUND:
        currentStep.value = CreateReviewStep.AssistantsSettings;
        break;
      case ReviewPreset.CUSTOM:
        currentStep.value = CreateReviewStep.PresetSelection;
        break;
      case ReviewPreset.DEFAULT:
        currentStep.value = CreateReviewStep.PresetSelection;
    }
  }

  return {
    preset,
    reviewName,
    levelOfEvidence,
    reset,
    createReview,
    currentStep,
    goToNextStep,
    goToPreviousStep,
    isCurrentStepValid,
    deviceHasAlternativeNames,
    manufacturerHasAlternativeNames,
    isGatheringBackgroundInformation,
    medicalBackgroundConditionNames,
    timeFrame,
    isPmsDeviceAndManufacturerNamesValid,
    autoImportPmsItems,
    autoImportMedicalBackgroundItems,
    generateReviewName,
    deleteMedicalBackgroundConditionName,
    reviewPresets,
    autoImportNotifiedBodyItems,
    isReviewAutomated,
  };
}
